<template>
  <div class="flex flex-col gap-4">
    <!-- Title block (MaRu print header) -->
    <div class="flex flex-wrap items-end justify-between gap-x-6 gap-y-2">
      <div>
        <p class="text-lg font-medium leading-tight">{{ result.title?.report }}</p>
        <p class="text-sm text-gray-600">
          {{ result.title?.location }}
          <span v-if="result.title?.location && result.title?.period" class="mx-1 text-gray-400">·</span>
          {{ result.title?.period }}
        </p>
      </div>
      <dl class="text-xs text-gray-500 text-right tabular-nums">
        <div class="flex gap-2 justify-end">
          <dt>{{ t('messages.transactions.analytics.result.compiled') }}</dt>
          <dd>{{ formatDate(result.generated_at) }}</dd>
        </div>
        <div class="flex gap-2 justify-end">
          <dt>{{ t('messages.transactions.analytics.result.data_as_of') }}</dt>
          <dd data-testid="txn-analytics-data-as-of">{{ formatDate(result.data_as_of) }}</dd>
        </div>
      </dl>
    </div>

    <Message
      v-if="store.isStale"
      severity="warn"
      size="small"
      :closable="false"
      data-testid="txn-analytics-stale"
    >
      {{
        t('messages.transactions.analytics.state.stale', {
          as_of: formatDate(result.data_as_of),
          import: formatDate(result.last_import_at),
        })
      }}
    </Message>

    <!-- Actions + table -->
    <slot />

    <!-- Kokkuvõte (server-composed sentence, D9) -->
    <div
      v-if="result.summary"
      class="rounded-md border border-gray-200 bg-surface-50 px-4 py-3"
      data-testid="txn-analytics-summary"
    >
      <p class="text-xs uppercase tracking-wide text-gray-500 mb-1">
        {{ t('messages.transactions.analytics.summary.title') }}
      </p>
      <p class="text-sm">{{ result.summary }}</p>
    </div>

    <!-- Footnotes (server-composed) -->
    <ol v-if="result.footnotes?.length" class="text-xs text-gray-500 list-decimal pl-4 space-y-0.5" data-testid="txn-analytics-footnotes">
      <li v-for="(note, index) in result.footnotes" :key="index">{{ note }}</li>
    </ol>
  </div>
</template>

<script setup>
import { useI18n } from 'vue-i18n'
import { useFormatDate } from '@/composables/useFormatDate'
import { useAnalyticsStore } from '@/stores/analyticsStore'

/**
 * RED-310 — the report "sheet" around a result: MaRu-style title block
 * (report / location · period / Koostatud / Andmed seisuga), the stale-data
 * warning when the store flags data older than 60 days, the default slot
 * for actions + table, then the server-composed Kokkuvõte sentence and the
 * footnotes. Summary and footnotes are never composed on the client.
 */
defineProps({
  result: { type: Object, required: true },
})

const { t } = useI18n()
const { formatDate } = useFormatDate()
const store = useAnalyticsStore()
</script>
